export interface CacheEntry<T = any> {
  data: T;
  timestamp: number;
  accessCount: number;
  lastAccessed: number;
  priority: 'high' | 'medium' | 'low';
  maxAge: number;
}

export interface CacheStats {
  totalEntries: number;
  cacheHitRate: number;
  averageAge: number;
  staleEntries: number;
}

export class CacheManagerUtility {
  private cache = new Map<string, CacheEntry>();
  private hitCount = 0;
  private missCount = 0;

  public set<T>(
    key: string, 
    data: T, 
    options: {
      priority?: 'high' | 'medium' | 'low';
      maxAge?: number;
    } = {}
  ): void {
    const { priority = 'medium', maxAge = 30 * 60 * 1000 } = options;
    
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      accessCount: 0,
      lastAccessed: Date.now(),
      priority,
      maxAge,
    };

    this.cache.set(key, entry);
  }

  public get<T>(key: string): CacheEntry<T> | null {
    const entry = this.cache.get(key);
    
    if (!entry) {
      this.missCount++;
      return null;
    }

    // Check if expired
    if (Date.now() - entry.timestamp > entry.maxAge) {
      this.cache.delete(key);
      this.missCount++;
      return null;
    }

    // Update access stats
    entry.accessCount++;
    entry.lastAccessed = Date.now();
    this.hitCount++;

    return entry as CacheEntry<T>;
  }

  public has(key: string): boolean {
    return this.cache.has(key) && !this.isExpired(key);
  }

  public delete(key: string): boolean {
    return this.cache.delete(key);
  }

  public clear(): void {
    this.cache.clear();
    this.hitCount = 0;
    this.missCount = 0;
  }

  public getStats(): CacheStats {
    const totalEntries = this.cache.size;
    const totalRequests = this.hitCount + this.missCount;
    const cacheHitRate = totalRequests > 0 ? (this.hitCount / totalRequests) * 100 : 0;

    let totalAge = 0;
    let staleEntries = 0;
    const now = Date.now();

    for (const entry of Array.from(this.cache.values())) {
      const age = now - entry.timestamp;
      totalAge += age;
      
      if (age > entry.maxAge) {
        staleEntries++;
      }
    }

    const averageAge = totalEntries > 0 ? totalAge / totalEntries : 0;

    return {
      totalEntries,
      cacheHitRate,
      averageAge,
      staleEntries,
    };
  }

  public pruneByAge(maxAge: number): number {
    let removedCount = 0;
    const now = Date.now();

    for (const [key, entry] of Array.from(this.cache.entries())) {
      if (now - entry.timestamp > maxAge) {
        this.cache.delete(key);
        removedCount++;
      }
    }

    return removedCount;
  }

  public pruneByUsage(keepTopN: number): number {
    if (this.cache.size <= keepTopN) {
      return 0;
    }

    // Sort by access count and last accessed time
    const entries = Array.from(this.cache.entries()).sort((a, b) => {
      const [, entryA] = a;
      const [, entryB] = b;
      
      // First sort by priority
      const priorityOrder = { high: 3, medium: 2, low: 1 };
      const priorityDiff = priorityOrder[entryB.priority] - priorityOrder[entryA.priority];
      if (priorityDiff !== 0) return priorityDiff;
      
      // Then by access count
      const accessDiff = entryB.accessCount - entryA.accessCount;
      if (accessDiff !== 0) return accessDiff;
      
      // Finally by last accessed time
      return entryB.lastAccessed - entryA.lastAccessed;
    });

    // Keep top N, remove the rest
    const toRemove = entries.slice(keepTopN);
    let removedCount = 0;

    for (const [key] of toRemove) {
      this.cache.delete(key);
      removedCount++;
    }

    return removedCount;
  }

  public getKeys(): string[] {
    return Array.from(this.cache.keys());
  }

  public getSize(): number {
    return this.cache.size;
  }

  private isExpired(key: string): boolean {
    const entry = this.cache.get(key);
    if (!entry) return true;
    
    return Date.now() - entry.timestamp > entry.maxAge;
  }
}

export default CacheManagerUtility;